/**
 * Tiny fetch wrapper for the Dovach API. Hard-codes the L1 dev base URL so
 * the bundle has no env baked in; stage-2 swaps via Vite's `import.meta.env`.
 *
 * Errors are surfaced as `{ status, detail }` so callers can branch on the
 * stable `detail` string without parsing prose.
 */

// API_BASE is env-overridable for parallel test environments / CI.
// Default targets the mkcert-served local dev API on TLS.
const API_BASE: string =
  (import.meta as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ??
  "https://api.dovach.local:8080";

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(`API ${status}: ${detail}`);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  init: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...((init.headers as Record<string, string> | undefined) ?? {}),
  };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const resp = await fetch(API_BASE + path, {
    method,
    headers,
    body: payload,
    credentials: "omit",
    ...init,
  });
  if (!resp.ok) {
    let detail = `http_${resp.status}`;
    try {
      const j = await resp.json();
      if (j && typeof j.detail === "string") detail = j.detail;
    } catch {
      // ignore
    }
    throw new ApiError(resp.status, detail);
  }
  if (resp.status === 204) return undefined as T;
  return resp.json() as Promise<T>;
}

// --------------------------------------------------- types

export interface InviteResponse {
  account_id: string;
  client_name: string;
  campaign: { name: string; period_label: string; deadline_at: string };
  requested_docs: string[];
  cert_blob: string | null;
}

export interface RejectedFile {
  file_id: string;
  document_label: string | null;
  reason: string;
  uploaded_at: string;
}

export interface InviteState {
  uploaded: { document_label: string; count: number }[];
  total_bytes: number;
  remaining_bytes_for_run: number;
  rejected_files: RejectedFile[];
}

export interface OtpVerifyResponse {
  upload_token: string;
  expires_at: string;
}

export interface UploadResponse {
  file_id: string;
}

// --------------------------------------------------- public surface

export const api = {
  getInvite: (id: string) => request<InviteResponse>("GET", `/v1/invite/${id}`),
  getInviteState: (id: string) => request<InviteState>("GET", `/v1/invite/${id}/state`),
  otpSend: (id: string) =>
    request<{ resend_after_seconds: number }>("POST", `/v1/invite/${id}/otp/send`, {}),
  otpVerify: (id: string, code: string) =>
    request<OtpVerifyResponse>("POST", `/v1/invite/${id}/otp/verify`, { code }),

  upload: (
    token: string,
    file: File,
    documentLabel?: string,
    signal?: AbortSignal,
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    const q = new URLSearchParams({ token });
    if (documentLabel) q.set("document_label", documentLabel);
    return request<UploadResponse>(
      "POST",
      `/v1/upload?${q.toString()}`,
      fd,
      { signal },
    );
  },

  /**
   * uploadWithProgress is the XHR-backed variant of `upload` that exposes
   * byte-level progress via `onProgress`. We use this for the visible
   * progress bar; the plain `upload` is kept for callers that don't
   * need progress (e.g. unit tests).
   */
  uploadWithProgress: (
    token: string,
    file: File,
    documentLabel: string | undefined,
    onProgress: (loaded: number, total: number) => void,
    signal?: AbortSignal,
  ): Promise<UploadResponse> => {
    return new Promise((resolve, reject) => {
      const fd = new FormData();
      fd.append("file", file);
      const q = new URLSearchParams({ token });
      if (documentLabel) q.set("document_label", documentLabel);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", API_BASE + `/v1/upload?${q.toString()}`);
      xhr.responseType = "json";

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded, e.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve((xhr.response as UploadResponse) ?? { file_id: "" });
        } else {
          // Try to extract { detail } from the JSON body, fall back to
          // an http_<status> string so the existing error-mapping in
          // InviteFlow.translateApiError works unchanged.
          const detail =
            (xhr.response as { detail?: string } | null)?.detail ??
            `http_${xhr.status}`;
          reject(new ApiError(xhr.status, detail));
        }
      };
      xhr.onerror = () => reject(new ApiError(0, "network_disconnect"));
      xhr.onabort = () => {
        const err = new Error("aborted");
        err.name = "AbortError";
        reject(err);
      };

      if (signal) {
        if (signal.aborted) {
          xhr.abort();
          return;
        }
        signal.addEventListener("abort", () => xhr.abort());
      }

      xhr.send(fd);
    });
  },
};
